import { calculateScoreEquivalent, calculateVolumeEquivalent } from '../../utils/volume';
import { isStrengthHistoryCategory } from './workoutExerciseHistoryCategory';

const positiveNumber = value => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
);

export const isCurrentWorkoutHistoryPoint = (point, options = {}) => {
  if (!point) return false;
  const workoutId = options.currentWorkoutId;
  const workoutDate = options.currentWorkoutDate;
  if (workoutId && point.workoutId) return String(point.workoutId) === String(workoutId);
  const pointTime = point?.workoutDate ? new Date(point.workoutDate).getTime() : NaN;
  const workoutTime = workoutDate ? new Date(workoutDate).getTime() : NaN;
  if (Number.isFinite(pointTime)
    && Number.isFinite(workoutTime)
    && Math.abs(pointTime - workoutTime) < 12 * 60 * 60 * 1000) return true;
  return Number.isFinite(options.currentPointX)
    && Number.isFinite(point.t)
    && Math.abs(point.t - options.currentPointX) < 1;
};

export const findCurrentWorkoutHistoryPoint = (points = [], options = {}, isDisplayable = () => true) => (
  (Array.isArray(points) ? points : []).find(point => (
    isDisplayable(point) && isCurrentWorkoutHistoryPoint(point, options)
  )) || null
);

export const calculateCompletedWorkingTotals = (
  sets = [], exerciseMeta = {}, bodyweight = 0, warmupIndex = null
) => {
  const workingSets = (Array.isArray(sets) ? sets : []).filter((set, index) => (
    set?.complete === true && index !== warmupIndex
  ));
  const volume = workingSets.reduce((sum, set) => (
    sum + calculateVolumeEquivalent(set, exerciseMeta, bodyweight)
  ), 0);
  const score = workingSets.reduce((sum, set) => (
    sum + calculateScoreEquivalent(set, exerciseMeta, bodyweight)
  ), 0);
  return {
    volume: Number.isFinite(volume) && volume > 0 ? volume : null,
    score: Number.isFinite(score) && score > 0 ? score : null,
  };
};

export const calculateCompletedHistoryMetricTotals = (sets = []) => {
  const completedSets = (Array.isArray(sets) ? sets : []).filter(set => set?.complete === true);
  const distance = completedSets.reduce((sum, set) => sum + (positiveNumber(Number(set.distance)) || 0), 0);
  const calories = completedSets.reduce((sum, set) => sum + (positiveNumber(Number(set.calories)) || 0), 0);
  const repetitions = completedSets.reduce((sum, set) => sum + (positiveNumber(Number(set.repetitions)) || 0), 0);
  const durationSeconds = completedSets.reduce((sum, set) => {
    const durationMs = positiveNumber(Number(set.duration));
    return sum + (durationMs ? durationMs / 1000 : 0);
  }, 0);

  return {
    distance: positiveNumber(distance),
    durationSeconds: positiveNumber(durationSeconds),
    calories: positiveNumber(calories),
    repetitions: positiveNumber(repetitions),
  };
};

export const calculateCompletedCardioTotals = (sets = []) => {
  const { distance, durationSeconds } = calculateCompletedHistoryMetricTotals(sets);
  return { distance, durationSeconds };
};

export const mergeCurrentWorkoutHistoryPoint = (history = [], options = {}) => {
  const rows = Array.isArray(history) ? history.map(row => ({ ...row })) : [];
  const category = String(options.category || '').trim().toLowerCase();
  const completedVolume = positiveNumber(options.completedVolume);
  const completedScore = positiveNumber(options.completedScore);
  const completedDistance = positiveNumber(options.completedDistance);
  const completedDurationSeconds = positiveNumber(options.completedDurationSeconds);
  const completedCalories = positiveNumber(options.completedCalories);
  const completedRepetitions = positiveNumber(options.completedRepetitions);
  if (!completedVolume && !completedScore && !completedDistance && !completedDurationSeconds && !completedCalories && !completedRepetitions) return rows;
  if (!options.currentWorkoutId || !options.currentWorkoutDate) return rows;

  const liveFields = {};
  if (category === 'sport') {
    if (completedVolume) liveFields.totalEquivalentVolume = completedVolume;
    if (completedScore) liveFields.totalEquivalentScore = completedScore;
    if (completedCalories) liveFields.totalCalories = completedCalories;
  } else if (isStrengthHistoryCategory(category) || category === 'static') {
    if (completedVolume) liveFields.totalVolume = completedVolume;
    if (completedScore) liveFields.totalScore = completedScore;
    if (completedRepetitions) liveFields.totalRepetitions = completedRepetitions;
  } else if (category === 'cardio') {
    if (completedDistance) liveFields.totalDistance = completedDistance;
    if (completedDurationSeconds) liveFields.totalDurationSeconds = completedDurationSeconds;
  } else if (category === 'stretching') {
    if (completedDurationSeconds) liveFields.totalDurationSeconds = completedDurationSeconds;
  } else {
    return rows;
  }

  const currentIndex = rows.findIndex(row => (
    isCurrentWorkoutHistoryPoint(row, options)
  ));
  if (currentIndex >= 0) {
    rows[currentIndex] = { ...rows[currentIndex], ...liveFields };
    return rows;
  }

  rows.push({
    workoutId: String(options.currentWorkoutId),
    workoutDate: options.currentWorkoutDate,
    ...liveFields,
  });
  return rows;
};
